import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PATH_SEGMENTS, route } from '../config/paths';
import { config } from '../config';
import { extractApiError } from '../services/api';
import {
  liveAssistCloseSession,
  liveAssistCreateSession,
  liveAssistGetSession,
  liveAssistIngestText,
  liveAssistProcessTurn,
  liveAssistStartAudio,
  liveAssistStopAudio,
  liveAssistIngestTranscriptFile,
  liveAssistTranscribeFile,
  liveAssistUploadRecording,
  LiveAssistSessionPayload,
} from '../services/liveAssistClient';
import { orchestratorResultToTurn } from '../utils/liveAssistMapper';
import {
  isBrowserTtsSupported,
  preloadBrowserTtsVoices,
  speakVoiceBotText,
  stopVoiceBotSpeech,
} from '../utils/voiceBotTts';
import {
  beginSessionAudioFromGesture,
  bindAudioContextResumeRecovery,
  buildMediaWebSocketUrl,
  closeSessionAudio,
  LiveAssistMediaClient,
  type SessionAudio,
} from '../utils/liveAssistMediaWs';
import AgentAssistCard from '../components/AgentAssistCard';
import AgentAssistLiveCard from '../components/AgentAssistLiveCard';
import LiveAssistCard from '../components/LiveAssistCard';
import { TurnAnalysisTurn } from '../types/api';
import '../styles/screens/LiveAssist.css';

type SttSpeaker = 'customer' | 'agent';

const AGENT_ASSIST_UI_STORAGE_KEY = 'liveAssist.agentAssistUiMode';
type AgentAssistUiMode = 'compact' | 'focus' | 'focus_compact' | 'timeline';

function interactionIdFromSession(s: LiveAssistSessionPayload | null): number | null {
  if (!s) return null;
  if (typeof s.interaction_id === 'number' && s.interaction_id > 0) return s.interaction_id;
  const rs = s.runtime_state as { interaction_id?: number } | undefined;
  if (rs && typeof rs.interaction_id === 'number' && rs.interaction_id > 0) return rs.interaction_id;
  return null;
}

const LiveAssistLive: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeInteractionParam = searchParams.get('resume');
  const [mode, setMode] = useState('agent_assist');
  const [inputSource, setInputSource] = useState('audio_mic');
  const [department, setDepartment] = useState('');
  const [viewerIdsRaw, setViewerIdsRaw] = useState('');

  const [session, setSession] = useState<LiveAssistSessionPayload | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [allTurns, setAllTurns] = useState<TurnAnalysisTurn[]>([]);

  // Voice bot — populated whenever the backend returns result.voice_bot.speech
  const [voiceBotLog, setVoiceBotLog] = useState<
    Array<{ turn: number; speech: string; focusAction: string; actionsPerformed: string[] }>
  >([]);

  const [manualText, setManualText] = useState('');
  const [manualSpeaker, setManualSpeaker] = useState<SttSpeaker>('customer');
  const [sttSpeaker, setSttSpeaker] = useState<SttSpeaker>('customer');
  const [transcribePath, setTranscribePath] = useState('');
  const [transcriptIngestPath, setTranscriptIngestPath] = useState('');

  const [sttOn, setSttOn] = useState(false);
  const [sttSupported, setSttSupported] = useState(false);
  const [ttsSupported, setTtsSupported] = useState(false);
  /** Live partial text from the browser STT (not yet final). */
  const [sttInterim, setSttInterim] = useState('');
  /** Final phrases recognized and sent to ingest-text (newest last). */
  const [voiceLines, setVoiceLines] = useState<
    Array<{ id: string; speaker: SttSpeaker; text: string; ts: number; moments?: string[] }>
  >([]);
  const [infoBanner, setInfoBanner] = useState<string | null>(null);
  const [transcribingFile, setTranscribingFile] = useState(false);
  const [serverMediaActive, setServerMediaActive] = useState(false);
  const [serverMediaStatus, setServerMediaStatus] = useState<string | null>(null);
  const [micMuted, setMicMuted] = useState(false);
  const [agentAssistUiMode, setAgentAssistUiMode] = useState<AgentAssistUiMode>(() => {
    if (typeof window === 'undefined') return 'focus';
    try {
      const v = window.localStorage.getItem(AGENT_ASSIST_UI_STORAGE_KEY);
      if (v === 'compact' || v === 'focus' || v === 'focus_compact' || v === 'timeline') return v;
    } catch {
      /* ignore */
    }
    return 'focus';
  });
  const recognitionRef = useRef<{ stop: () => void; abort: () => void } | null>(null);
  const handleIngestFinalRef = useRef<
    (text: string, speaker: SttSpeaker, startMs?: number, endMs?: number) => Promise<void>
  >(async () => {});
  const sttSpeakerRef = useRef(sttSpeaker);
  sttSpeakerRef.current = sttSpeaker;
  /** Original mic audio for replay (`audios/{interaction_id}.webm`); separate from Web Speech STT. */
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  /** Relative timing base (epoch ms) for caption segments in current live session. */
  const captionBaseMsRef = useRef<number | null>(null);
  /** Last finalized caption end-time (relative ms) to keep monotonic segments. */
  const lastCaptionEndMsRef = useRef<number>(0);
  /**
   * Wall-clock ms when browser MediaRecorder started (audio ingress).
   * Speech `start_ms`/`end_ms` must share this origin so DB rows line up with the replay file waveform.
   */
  const recordingTimelineBaseMsRef = useRef<number | null>(null);
  /** Serialize ingest-text so stop/close never races ahead of in-flight HTTP persists. */
  const ingestTailRef = useRef<Promise<unknown>>(Promise.resolve());
  /** Latest session id for STT flush callbacks (avoid stale closure when closing). */
  const sessionIdRef = useRef<string | null>(null);
  /** Latest browser STT interim line — often never becomes `isFinal` if we abort(); flush on shutdown. */
  const sttInterimTextRef = useRef('');
  const voiceBotAudioEnabledRef = useRef(true);
  const sessionModeRef = useRef(mode);
  const audioIngressModeRef = useRef('direct_assemblyai');
  const serverStreamingRef = useRef(false);
  const wsBotSpeechTurnsRef = useRef<Set<number>>(new Set());
  const mediaClientRef = useRef<LiveAssistMediaClient | null>(null);
  const audioStartInFlightRef = useRef(false);
  const sessionAudioRef = useRef<SessionAudio | null>(null);
  const unbindResumeRecoveryRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as Window & { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
    setSttSupported(typeof w.SpeechRecognition !== 'undefined' || typeof w.webkitSpeechRecognition !== 'undefined');
    setTtsSupported(isBrowserTtsSupported());
    preloadBrowserTtsVoices();
    const onVoices = () => preloadBrowserTtsVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', onVoices);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', onVoices);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(AGENT_ASSIST_UI_STORAGE_KEY, agentAssistUiMode);
    } catch {
      /* ignore */
    }
  }, [agentAssistUiMode]);

  useEffect(() => {
    sessionIdRef.current = session?.session_id ?? null;
    voiceBotAudioEnabledRef.current = session?.voice_bot_audio_enabled !== false;
    sessionModeRef.current = session?.mode ?? mode;
    if (session?.audio_ingress_mode) {
      audioIngressModeRef.current = session.audio_ingress_mode;
    }
  }, [session?.session_id, session?.voice_bot_audio_enabled, session?.mode, session?.audio_ingress_mode, mode]);

  const sessionId = session?.session_id ?? null;
  const currentTurnNumber =
    allTurns.length > 0 ? allTurns[allTurns.length - 1].turn_number : 0;

  const currentTurnForAssist = useMemo(() => {
    return (
      allTurns.find((t) => t.turn_number === currentTurnNumber) ??
      (allTurns.length > 0 ? allTurns[allTurns.length - 1] : null)
    );
  }, [allTurns, currentTurnNumber]);

  const appendTurn = useCallback((turnNum: number, speaker: string, text: string, result: Record<string, unknown>) => {
    setAllTurns((prev) => [...prev, orchestratorResultToTurn(turnNum, speaker, text, result)]);
  }, []);

  const momentNamesFromResult = (result: Record<string, unknown> | undefined): string[] => {
    const raw = result?.moments;
    if (!Array.isArray(raw)) return [];
    const names: string[] = [];
    for (const item of raw) {
      if (typeof item === 'string' && item.trim()) names.push(item.trim());
      else if (item && typeof item === 'object' && 'name' in item) {
        const n = String((item as { name?: unknown }).name || '').trim();
        if (n) names.push(n);
      }
    }
    return names;
  };

  const attachMomentsToLastVoiceLine = (speaker: SttSpeaker, names: string[]) => {
    if (!names.length) return;
    setVoiceLines((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].speaker === speaker) {
          next[i] = { ...next[i], moments: names };
          break;
        }
      }
      return next;
    });
  };

  /** Action names from voice_bot.agent_actions object keys. */
  const agentActionNames = (vb: Record<string, unknown> | undefined): string[] => {
    const aa = vb?.agent_actions;
    if (aa && typeof aa === 'object' && !Array.isArray(aa)) {
      return Object.keys(aa as Record<string, unknown>);
    }
    return [];
  };

  /** Extract voice_bot payload from a raw turn result, if present. */
  const extractVoiceBot = useCallback(
    (result: Record<string, unknown>) => {
      const vb = result.voice_bot as Record<string, unknown> | undefined;
      if (!vb || typeof vb.speech !== 'string' || !vb.speech.trim()) return null;
      const fs = vb.focus_step as Record<string, unknown> | undefined;
      return {
        speech: vb.speech.trim(),
        focusAction: String(fs?.action_name ?? ''),
        actionsPerformed: agentActionNames(vb),
      };
    },
    []
  );

  const maybeSpeakVoiceBot = useCallback((speech: string) => {
    if (serverStreamingRef.current) return;
    if (audioIngressModeRef.current === 'backend_streaming') return;
    if (sessionModeRef.current !== 'voice_bot') return;
    if (!voiceBotAudioEnabledRef.current) return;
    if (!isBrowserTtsSupported()) return;
    speakVoiceBotText(speech);
  }, []);

  const appendVoiceBot = useCallback(
    (turnNum: number, result: Record<string, unknown>, speechOverride?: string) => {
      const vb = extractVoiceBot(result);
      const speech = (vb?.speech ?? speechOverride ?? '').trim();
      if (!speech) return;
      // Single claim point: HTTP /turns and media bot_speech both reach here.
      if (wsBotSpeechTurnsRef.current.has(turnNum)) return;
      wsBotSpeechTurnsRef.current.add(turnNum);
      const fs = (result.voice_bot as Record<string, unknown> | undefined)?.focus_step as
        | Record<string, unknown>
        | undefined;
      setVoiceBotLog((prev) => [
        ...prev,
        {
          turn: turnNum,
          speech,
          focusAction: vb?.focusAction ?? String(fs?.action_name ?? ''),
          actionsPerformed:
            vb?.actionsPerformed ??
            agentActionNames(result.voice_bot as Record<string, unknown> | undefined),
        },
      ]);
      const id = `bot-${turnNum}-${Date.now()}`;
      setVoiceLines((prev) => [
        ...prev,
        {
          id,
          speaker: 'agent' as SttSpeaker,
          text: `🤖 ${speech}`,
          ts: Date.now(),
          moments: momentNamesFromResult(result),
        },
      ]);
      maybeSpeakVoiceBot(speech);
    },
    [extractVoiceBot, maybeSpeakVoiceBot]
  );

  type VoiceBotAgentTurnPayload = {
    turn?: number;
    utterance?: string;
    result?: Record<string, unknown>;
  };

  const applyVoiceBotTurnFromApi = useCallback(
    (turn: number | undefined, result: Record<string, unknown> | undefined) => {
      if (typeof turn !== 'number' || !result) return;
      const vb = extractVoiceBot(result);
      const speech = vb?.speech ?? '(voice bot opening)';
      appendTurn(turn, 'agent', speech, result);
      appendVoiceBot(turn, result, speech);
    },
    [appendTurn, appendVoiceBot, extractVoiceBot]
  );

  const applyOpeningTurnIfPresent = useCallback(
    (data: { opening_turn?: { turn?: number; result?: Record<string, unknown> } }) => {
      const ot = data.opening_turn;
      if (!ot) return;
      applyVoiceBotTurnFromApi(ot.turn, ot.result);
    },
    [applyVoiceBotTurnFromApi]
  );

  /** Append customer + agent voice-bot turns returned by POST /turns or ingest-text. */
  const applyVoiceBotResponseTurns = useCallback(
    (
      data: {
        turn?: number;
        result?: Record<string, unknown>;
        agent_turn?: VoiceBotAgentTurnPayload;
        agent_turn_chain?: VoiceBotAgentTurnPayload[];
        agent_transcript_only?: VoiceBotAgentTurnPayload;
        opening_turn?: { turn?: number; result?: Record<string, unknown> };
      },
      customerSpeaker: SttSpeaker,
      customerUtterance: string,
      opts?: { mirrorCustomerToTranscript?: boolean }
    ) => {
      applyOpeningTurnIfPresent(data);
      if (typeof data.turn !== 'number' || !data.result) return;
      appendTurn(data.turn, customerSpeaker, customerUtterance, data.result);
      // Manual turns never hit onSttFinal; browser STT already mirrored the line.
      if (opts?.mirrorCustomerToTranscript) {
        const trimmedCustomer = customerUtterance.trim();
        if (trimmedCustomer) {
          const id =
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `cust-${data.turn}-${Date.now()}`;
          setVoiceLines((prev) => [
            ...prev,
            {
              id,
              speaker: customerSpeaker,
              text: trimmedCustomer,
              ts: Date.now(),
              moments: momentNamesFromResult(data.result),
            },
          ]);
        }
      }

      const ghost = data.agent_transcript_only;
      if (typeof ghost?.turn === 'number' && ghost.result) {
        const ghostUtterance = String(ghost.utterance ?? '').trim();
        if (ghostUtterance) {
          appendTurn(ghost.turn, 'agent', ghostUtterance, ghost.result);
          appendVoiceBot(ghost.turn, ghost.result, ghostUtterance);
        }
      }

      const agentTurn = data.agent_turn;
      if (typeof agentTurn?.turn === 'number' && agentTurn.result) {
        const agentUtterance =
          String(agentTurn.utterance ?? '').trim() || '(voice bot)';
        appendTurn(agentTurn.turn, 'agent', agentUtterance, agentTurn.result);
        // Prefer agent_turn for transcript/log (correct agent turn id).
        appendVoiceBot(agentTurn.turn, agentTurn.result, agentUtterance);
      } else {
        // Fallback when API only attaches voice_bot on the customer result.
        appendVoiceBot(data.turn, data.result);
      }

      for (const chained of data.agent_turn_chain ?? []) {
        if (typeof chained.turn !== 'number' || !chained.result) continue;
        const utterance = String(chained.utterance ?? '').trim() || '(voice bot)';
        appendTurn(chained.turn, 'agent', utterance, chained.result);
        appendVoiceBot(chained.turn, chained.result);
      }
    },
    [appendTurn, appendVoiceBot, applyOpeningTurnIfPresent]
  );

  const refreshSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const s = await liveAssistGetSession(sessionId);
      setSession(s);
    } catch (e) {
      console.error(e);
    }
  }, [sessionId]);

  const captureSessionAudioFromGesture = useCallback((): SessionAudio => {
    unbindResumeRecoveryRef.current?.();
    unbindResumeRecoveryRef.current = null;
    if (sessionAudioRef.current) {
      closeSessionAudio(sessionAudioRef.current);
      sessionAudioRef.current = null;
    }
    const audio = beginSessionAudioFromGesture();
    sessionAudioRef.current = audio;
    unbindResumeRecoveryRef.current = bindAudioContextResumeRecovery(audio.captureContext);
    return audio;
  }, []);

  const releaseSessionAudio = useCallback(() => {
    unbindResumeRecoveryRef.current?.();
    unbindResumeRecoveryRef.current = null;
    closeSessionAudio(sessionAudioRef.current);
    sessionAudioRef.current = null;
  }, []);

  const stopServerMedia = useCallback(() => {
    mediaClientRef.current?.stop({ closeAudio: true });
    mediaClientRef.current = null;
    releaseSessionAudio();
    serverStreamingRef.current = false;
    setServerMediaActive(false);
    setServerMediaStatus(null);
    setSttInterim('');
  }, [releaseSessionAudio]);

  const startServerMedia = useCallback(
    async (mediaWsPath: string) => {
      if (!sessionIdRef.current) return;
      if (serverStreamingRef.current && mediaClientRef.current) return;
      const token = localStorage.getItem('token');
      if (!token) {
        setSessionError('Login required for server media streaming');
        return;
      }
      mediaClientRef.current?.stop({ closeAudio: false });
      mediaClientRef.current = null;
      stopVoiceBotSpeech();
      let held = sessionAudioRef.current;
      if (!held) held = captureSessionAudioFromGesture();
      let micStream: MediaStream;
      try {
        setServerMediaStatus('Requesting microphone…');
        micStream = held.micStream ?? (await held.micPromise);
        held.micStream = micStream;
      } catch {
        releaseSessionAudio();
        setSessionError('Microphone permission is required for voice bot mode');
        return;
      }
      const captureContext = held.captureContext;
      void captureContext.resume();
      const micTrack = micStream.getAudioTracks()[0];
      const micLabel = micTrack?.label?.trim() || 'Microphone';
      const wsUrl = buildMediaWebSocketUrl(config.api.baseUrl, mediaWsPath, token);
      const client = new LiveAssistMediaClient({
        onReady: () => {
          client.setSpeaker(sttSpeakerRef.current);
          const mutedHint = micTrack?.muted ? ' (browser reports mic muted — check tab/site icon)' : '';
          const isAgentAssist = sessionModeRef.current === 'agent_assist';
          setServerMediaStatus(
            isAgentAssist
              ? `Mic ready: ${micLabel}${mutedHint}. Choose agent/customer above, then speak.`
              : `Mic ready: ${micLabel}${mutedHint}. Speak as customer, then pause briefly.`
          );
          setServerMediaActive(true);
        },
        onPcmStreaming: () => {
          const isAgentAssist = sessionModeRef.current === 'agent_assist';
          setServerMediaStatus(
            isAgentAssist
              ? `Streaming mic audio (${micLabel}) — speaker: ${sttSpeakerRef.current}`
              : `Streaming mic audio (${micLabel}) — speak as customer, then pause briefly`
          );
        },
        onPartialStt: (text) => setSttInterim(text),
        onSttFinal: (text, speaker) => {
          const trimmed = text.trim();
          if (!trimmed) return;
          const spk: SttSpeaker = speaker === 'agent' ? 'agent' : 'customer';
          const id = crypto.randomUUID?.() ?? `ws-${Date.now()}`;
          setVoiceLines((prev) => [...prev, { id, speaker: spk, text: trimmed, ts: Date.now() }]);
          setSttInterim('');
        },
        onBotSpeech: (turn, text) => {
          if (turn == null || !text.trim()) return;
          appendVoiceBot(turn, { voice_bot: { speech: text } });
        },
        onTurnComplete: ({ turn, speaker, utterance, result }) => {
          appendTurn(turn, speaker, utterance, result);
          const names = momentNamesFromResult(result);
          const spk: SttSpeaker = speaker === 'agent' ? 'agent' : 'customer';
          attachMomentsToLastVoiceLine(spk, names);
        },
        onError: (message) => setSessionError(message),
        onClose: () => {
          serverStreamingRef.current = false;
          setServerMediaActive(false);
          setServerMediaStatus(null);
        },
      });
      client.attachSessionAudio(captureContext, micStream);
      client.setMicMuted(micMuted);
      mediaClientRef.current = client;
      serverStreamingRef.current = true;
      setServerMediaStatus('Connecting WebSocket…');
      try {
        await client.connect(wsUrl);
      } catch (e) {
        stopServerMedia();
        const { message } = await extractApiError(e);
        setSessionError(message || 'Failed to start server media stream');
      }
    },
    [appendTurn, appendVoiceBot, captureSessionAudioFromGesture, micMuted, releaseSessionAudio, stopServerMedia]
  );

  useEffect(() => {
    mediaClientRef.current?.setMicMuted(micMuted);
  }, [micMuted]);

  useEffect(() => {
    if (!serverMediaActive) return;
    mediaClientRef.current?.setSpeaker(sttSpeaker);
  }, [serverMediaActive, sttSpeaker]);

  const startMicCaptureForReplay = useCallback(async () => {
    if (inputSource !== 'audio_mic') return;
    if (typeof MediaRecorder === 'undefined') return;
    if (mediaRecorderRef.current?.state === 'recording') return;
    try {
      const held = sessionAudioRef.current;
      const stream =
        held?.micStream ??
        (held ? await held.micPromise : await navigator.mediaDevices.getUserMedia({ audio: true }));
      if (held) held.micStream = stream;
      mediaStreamRef.current = stream;
      recordedChunksRef.current = [];
      let mime: string | undefined;
      for (const m of ['audio/webm;codecs=opus', 'audio/webm']) {
        if (MediaRecorder.isTypeSupported(m)) {
          mime = m;
          break;
        }
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mr.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mr.start(1000);
      recordingTimelineBaseMsRef.current = Date.now();
      mediaRecorderRef.current = mr;
    } catch (e) {
      console.warn('Could not start MediaRecorder for live replay audio:', e);
    }
  }, [inputSource]);

  const runStartAudioIngress = useCallback(
    async (sid: string, sessionPayload?: LiveAssistSessionPayload) => {
      if (audioStartInFlightRef.current) return;
      if (serverStreamingRef.current && mediaClientRef.current) return;
      audioStartInFlightRef.current = true;
      try {
        setSessionError(null);
        const data = await liveAssistStartAudio(sid);
        if (data.session) {
          setSession(data.session as LiveAssistSessionPayload);
        } else if (sessionPayload) {
          setSession(sessionPayload);
        }
        const ingressMode =
          (data.session as LiveAssistSessionPayload | undefined)?.audio_ingress_mode ??
          sessionPayload?.audio_ingress_mode ??
          audioIngressModeRef.current;
        if (ingressMode !== 'backend_streaming') {
          applyVoiceBotTurnFromApi(
            typeof data.turn === 'number' ? data.turn : undefined,
            data.result && typeof data.result === 'object' ? (data.result as Record<string, unknown>) : undefined
          );
        }
        const mediaPath = typeof data.media_ws_path === 'string' ? data.media_ws_path : null;
        if (mediaPath || ingressMode === 'backend_streaming') {
          if (mediaPath) {
            await startServerMedia(mediaPath);
          } else {
            setSessionError('Server uses backend_streaming but did not return media_ws_path');
          }
        } else {
          await startMicCaptureForReplay();
        }
      } finally {
        audioStartInFlightRef.current = false;
      }
    },
    [applyVoiceBotTurnFromApi, startMicCaptureForReplay, startServerMedia]
  );

  const finalizeMicCapture = useCallback(async (): Promise<{ blob: Blob; filename: string } | null> => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') {
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
      return null;
    }
    return new Promise((resolve) => {
      const done = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'm4a' : 'webm';
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        recordedChunksRef.current = [];
        if (!blob.size) {
          resolve(null);
          return;
        }
        resolve({ blob, filename: `live-assist.${ext}` });
      };
      mr.addEventListener('stop', done, { once: true });
      mr.stop();
    });
  }, []);

  const parseViewers = (): number[] => {
    return viewerIdsRaw
      .split(/[\s,]+/)
      .map((x) => parseInt(x.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  };

  const handleCreate = async () => {
    setSessionError(null);
    setInfoBanner(null);
    setBusy(true);
    const needsMicUnlock = mode === 'voice_bot' && inputSource === 'audio_mic';
    if (needsMicUnlock) {
      if (mediaClientRef.current || sessionAudioRef.current) stopServerMedia();
      captureSessionAudioFromGesture();
    }
    try {
      const viewers = parseViewers();
      const resumeId = resumeInteractionParam ? parseInt(resumeInteractionParam.trim(), 10) : NaN;
      const data = await liveAssistCreateSession({
        mode,
        input_source_type: inputSource,
        department: department.trim() || undefined,
        viewer_user_dpt_ids: viewers.length > 0 ? viewers : undefined,
        ...(Number.isFinite(resumeId) && resumeId > 0 ? { interaction_id: resumeId } : {}),
      });
      setSession(data.session);
      sessionModeRef.current = data.session.mode;
      audioIngressModeRef.current = data.session.audio_ingress_mode ?? 'direct_assemblyai';
      voiceBotAudioEnabledRef.current = data.session.voice_bot_audio_enabled !== false;
      wsBotSpeechTurnsRef.current.clear();
      setAllTurns([]);
      setVoiceBotLog([]);
      setVoiceLines([]);
      setSttInterim('');
      captionBaseMsRef.current = null;
      lastCaptionEndMsRef.current = 0;
      recordingTimelineBaseMsRef.current = null;
      ingestTailRef.current = Promise.resolve();
      sessionIdRef.current = data.session.session_id;
      const isServerVoiceBot =
        data.session.mode === 'voice_bot' &&
        inputSource === 'audio_mic' &&
        data.session.audio_ingress_mode === 'backend_streaming' &&
        data.session.voice_bot_audio_enabled !== false;

      if (!isServerVoiceBot) {
        applyVoiceBotTurnFromApi(data.turn, data.result);
        if (typeof data.turn === 'number') {
          wsBotSpeechTurnsRef.current.add(data.turn);
        }
      }

      if (isServerVoiceBot) {
        await runStartAudioIngress(data.session.session_id, data.session);
      } else if (needsMicUnlock && !isServerVoiceBot) {
        /* Replay MediaRecorder can reuse the same stream; keep it. */
      }
    } catch (e) {
      releaseSessionAudio();
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleStartAudio = async () => {
    if (!sessionId) return;
    setBusy(true);
    if (!sessionAudioRef.current && inputSource === 'audio_mic') {
      captureSessionAudioFromGesture();
    }
    try {
      await runStartAudioIngress(sessionId, session ?? undefined);
      await refreshSession();
    } catch (e) {
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleManualTurn = async () => {
    if (!sessionId || !manualText.trim()) return;
    setSessionError(null);
    setBusy(true);
    try {
      const now = Date.now();
      if (captionBaseMsRef.current == null) {
        captionBaseMsRef.current = recordingTimelineBaseMsRef.current ?? now;
        lastCaptionEndMsRef.current = 0;
      }
      const relStart = lastCaptionEndMsRef.current;
      const relEnd = Math.max(relStart + 1, now - captionBaseMsRef.current);
      lastCaptionEndMsRef.current = relEnd;
      const data = await liveAssistProcessTurn(sessionId, {
        speaker: session?.mode === 'voice_bot' ? 'customer' : manualSpeaker,
        utterance: manualText.trim(),
        start_ms: relStart,
        end_ms: relEnd,
      });
      const customerSpeaker: SttSpeaker =
        session?.mode === 'voice_bot' ? 'customer' : manualSpeaker;
      applyVoiceBotResponseTurns(data, customerSpeaker, manualText.trim(), {
        mirrorCustomerToTranscript: true,
      });
      setManualText('');
      setSession(data.session);
    } catch (e) {
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleIngestFinal = useCallback(
    async (text: string, speaker: SttSpeaker, startMs?: number, endMs?: number) => {
      const sid = sessionIdRef.current;
      if (!sid || !text.trim()) return;
      const run = async () => {
        setSessionError(null);
        setBusy(true);
        try {
          const data = await liveAssistIngestText(sid, {
            text: text.trim(),
            speaker,
            is_final: true,
            start_ms: startMs,
            end_ms: endMs,
          });
          if (data.turn != null && data.result) {
            applyVoiceBotResponseTurns(data, speaker, text.trim());
          }
          if (data.session) setSession(data.session);
        } catch (e) {
          const { message } = await extractApiError(e);
          setSessionError(message);
        } finally {
          setBusy(false);
        }
      };
      const next = ingestTailRef.current.then(run, run);
      ingestTailRef.current = next.catch(() => undefined);
      await next;
    },
    [applyVoiceBotResponseTurns]
  );

  useEffect(() => {
    handleIngestFinalRef.current = handleIngestFinal;
  }, [handleIngestFinal]);

  /**
   * Web Speech often has not fired `isFinal` when the user stops captions or closes the session.
   * Calling `abort()` discards that audio. We `stop()`, wait for `onend` (bounded), then send any
   * remaining interim text as one final ingest so speech_text + process_turn are not truncated.
   */
  const finalizeBrowserSttFlushPending = useCallback(async (): Promise<void> => {
    const rec = recognitionRef.current as unknown as {
      stop: () => void;
      onend: ((this: unknown, ev: Event) => void) | null;
    } | null;
    if (rec) {
      await new Promise<void>((resolve) => {
        const maxMs = 1800;
        let settled = false;
        let tid: number | undefined;
        const finish = () => {
          if (settled) return;
          settled = true;
          if (tid !== undefined) window.clearTimeout(tid);
          resolve();
        };
        tid = window.setTimeout(finish, maxMs);
        const prevOnEnd = rec.onend;
        rec.onend = function (this: unknown, ev: Event) {
          try {
            prevOnEnd?.call(this, ev);
          } catch {
            /* ignore */
          }
          finish();
        };
        try {
          rec.stop();
        } catch {
          finish();
        }
      });
      recognitionRef.current = null;
      setSttOn(false);
    }

    const text = sttInterimTextRef.current.trim();
    sttInterimTextRef.current = '';
    setSttInterim('');
    if (!text || !sessionIdRef.current) return;

    const now = Date.now();
    if (captionBaseMsRef.current == null) {
      captionBaseMsRef.current = recordingTimelineBaseMsRef.current ?? now;
      lastCaptionEndMsRef.current = 0;
    }
    let relEnd = Math.max(0, now - captionBaseMsRef.current);
    let relStart = Math.min(lastCaptionEndMsRef.current, relEnd);
    if (relEnd <= relStart) {
      relEnd = relStart + 1;
    }
    lastCaptionEndMsRef.current = relEnd;

    const spk = sttSpeakerRef.current;
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `v-flush-${Date.now()}`;
    setVoiceLines((prev) => [...prev, { id, speaker: spk, text, ts: now }]);

    await handleIngestFinalRef.current(text, spk, relStart, relEnd);
  }, []);

  /** Hard stop (e.g. unmount or before starting a new recognition instance). */
  const abortBrowserSttImmediate = useCallback(() => {
    try {
      recognitionRef.current?.abort();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    sttInterimTextRef.current = '';
    setSttOn(false);
    setSttInterim('');
  }, []);

  const handleClose = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      if (serverStreamingRef.current) {
        stopServerMedia();
      } else {
        await finalizeBrowserSttFlushPending();
      }
      await ingestTailRef.current.catch(() => undefined);
      stopVoiceBotSpeech();
      let latest: LiveAssistSessionPayload | null = null;
      try {
        latest = await liveAssistGetSession(sessionId);
        setSession(latest);
      } catch {
        latest = session;
      }
      const rec = await finalizeMicCapture();
      const iid = interactionIdFromSession(latest);
      if (rec && iid != null && inputSource === 'audio_mic') {
        try {
          await liveAssistUploadRecording(sessionId, rec.blob, rec.filename);
        } catch (e) {
          console.error(e);
          const { message } = await extractApiError(e);
          setSessionError(message);
        }
      }
      await liveAssistCloseSession(sessionId);
      setSession(null);
      setAllTurns([]);
      setVoiceLines([]);
      setSttInterim('');
      captionBaseMsRef.current = null;
      lastCaptionEndMsRef.current = 0;
      recordingTimelineBaseMsRef.current = null;
      ingestTailRef.current = Promise.resolve();
    } catch (e) {
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleStopAudio = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      if (serverStreamingRef.current) {
        stopServerMedia();
      } else {
        await finalizeBrowserSttFlushPending();
      }
      await ingestTailRef.current.catch(() => undefined);
      await liveAssistStopAudio(sessionId);
      await refreshSession();
    } catch (e) {
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleTranscribe = async () => {
    if (!sessionId || !transcribePath.trim()) return;
    setSessionError(null);
    setInfoBanner(null);
    setBusy(true);
    setTranscribingFile(true);
    try {
      const data = await liveAssistTranscribeFile(sessionId, transcribePath.trim());
      if (data.session) {
        setSession(data.session);
      } else {
        await refreshSession();
      }
      const interactionId = data.interaction_id;
      const turns = data.turns_applied ?? 0;
      if (interactionId) {
        navigate(route(PATH_SEGMENTS.LIVE_ASSIST, PATH_SEGMENTS.LIVE_ASSIST_REPLAY, interactionId));
        return;
      }
      setInfoBanner(`Transcribed ${turns} turn(s).`);
    } catch (e) {
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setTranscribingFile(false);
      setBusy(false);
    }
  };

  const handleIngestTranscriptFile = async () => {
    if (!sessionId || !transcriptIngestPath.trim()) return;
    setSessionError(null);
    setInfoBanner(null);
    setBusy(true);
    setTranscribingFile(true);
    try {
      const data = await liveAssistIngestTranscriptFile(sessionId, transcriptIngestPath.trim());
      if (data.session) {
        setSession(data.session);
      } else {
        await refreshSession();
      }
      const interactionId = data.interaction_id;
      if (interactionId) {
        navigate(route(PATH_SEGMENTS.LIVE_ASSIST, PATH_SEGMENTS.LIVE_ASSIST_REPLAY, interactionId));
        return;
      }
      setInfoBanner('Transcript file ingested.');
    } catch (e) {
      const { message } = await extractApiError(e);
      setSessionError(message);
    } finally {
      setTranscribingFile(false);
      setBusy(false);
    }
  };

  const startStt = () => {
    if (!sessionId) return;
    const w = window as Window & { SpeechRecognition?: new () => any; webkitSpeechRecognition?: new () => any };
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) {
      setSessionError('Speech recognition is not available in this browser.');
      return;
    }
    abortBrowserSttImmediate();
    /** Same origin as MediaRecorder when present, else wall clock when captions start. */
    const recordingBase = recordingTimelineBaseMsRef.current;
    if (captionBaseMsRef.current == null) {
      captionBaseMsRef.current = recordingBase ?? Date.now();
      lastCaptionEndMsRef.current = 0;
    } else if (recordingBase != null) {
      // Mid-call mic restart: keep timeline origin and last segment end so the first
      // utterance after restart does not get start_ms=0 (only end_ms was correct before).
      captionBaseMsRef.current = recordingBase;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (event: any) => {
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const res = event.results[i];
        if (!res.isFinal) {
          interim += res[0]?.transcript ?? '';
        }
      }
      setSttInterim(interim);
      sttInterimTextRef.current = interim;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (!res.isFinal) continue;
        const t = (res[0]?.transcript ?? '').trim();
        if (!t) continue;
        const spk = sttSpeakerRef.current;
        const id =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `v-${Date.now()}-${i}`;
        const now = Date.now();
        if (captionBaseMsRef.current == null) {
          captionBaseMsRef.current = recordingTimelineBaseMsRef.current ?? now;
          lastCaptionEndMsRef.current = 0;
        }
        let relEnd = Math.max(0, now - captionBaseMsRef.current);
        let relStart = Math.min(lastCaptionEndMsRef.current, relEnd);
        if (relEnd <= relStart) {
          relEnd = relStart + 1;
        }
        lastCaptionEndMsRef.current = relEnd;
        setVoiceLines((prev) => [...prev, { id, speaker: spk, text: t, ts: now }]);
        void handleIngestFinalRef.current(t, spk, relStart, relEnd);
      }
    };
    rec.onerror = () => {
      setSttOn(false);
    };
    rec.onend = () => {
      setSttOn(false);
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setSttOn(true);
    } catch {
      setSessionError('Could not start microphone recognition.');
      setSttOn(false);
    }
  };

  useEffect(() => {
    return () => {
      abortBrowserSttImmediate();
      stopServerMedia();
    };
  }, [abortBrowserSttImmediate, stopServerMedia]);

  const audioActive = Boolean(session?.audio_provider_session_id) || serverMediaActive;
  const sessionInputSource = session?.input_source_type ?? inputSource;
  const textBotMode =
    session?.mode === 'voice_bot' && session?.voice_bot_audio_enabled === false;
  const serverStreamingMode =
    !textBotMode &&
    (serverMediaActive || session?.audio_ingress_mode === 'backend_streaming');
  const agentAssistServerStreaming = session?.mode === 'agent_assist' && serverStreamingMode;
  const autoServerVoiceBot =
    session?.mode === 'voice_bot' &&
    sessionInputSource === 'audio_mic' &&
    session?.audio_ingress_mode === 'backend_streaming' &&
    session?.voice_bot_audio_enabled !== false;
  const voiceBotAudioEnabled = session?.voice_bot_audio_enabled !== false;
  const voiceBotTtsActive =
    voiceBotAudioEnabled && ttsSupported && session?.mode === 'voice_bot' && !serverStreamingMode;

  return (
    <div className="live-assist" style={{ overflow: 'auto', position: 'relative' }}>
      {transcribingFile && (
        <div
          className="live-assist-transcribing-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 12,
            color: '#f8fafc',
            fontSize: '1.1rem',
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ fontWeight: 600 }}>Transcribing…</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>This may take a minute. You will be taken to replay when ready.</div>
        </div>
      )}
      <div className="live-assist-toolbar">
        <span className="live-assist-toolbar-label">Real-time</span>
        <Link className="live-assist-toolbar-link" to={route(PATH_SEGMENTS.LIVE_ASSIST)}>
          Live Assist home
        </Link>
        <Link className="live-assist-toolbar-link" to={route(PATH_SEGMENTS.LIVE_ASSIST, PATH_SEGMENTS.LIVE_ASSIST_REPLAY)}>
          Replay
        </Link>
      </div>

      <div className="live-assist-live-layout">
        <div className="live-assist-live-panel">
          <h2>Session</h2>
          {!sessionId ? (
            <div className="live-assist-live-form">
              {resumeInteractionParam && Number.isFinite(parseInt(resumeInteractionParam, 10)) && (
                <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 12px' }}>
                  Resuming interaction <strong>{resumeInteractionParam}</strong> from replay (orchestrator will warm from transcript).
                </p>
              )}
              <label htmlFor="la-mode">Mode</label>
              <select id="la-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="agent_assist">agent_assist</option>
                <option value="voice_bot">voice_bot</option>
                <option value="demo_replay">demo_replay</option>
                <option value="observer_monitor">observer_monitor</option>
              </select>
              <label htmlFor="la-input">Input source</label>
              <select id="la-input" value={inputSource} onChange={(e) => setInputSource(e.target.value)}>
                <option value="text_manual">text_manual</option>
                <option value="audio_mic">audio_mic</option>
                <option value="audio_file">audio_file</option>
                <option value="external_stream">external_stream</option>
              </select>
              <label htmlFor="la-dept">Department (optional)</label>
              <input id="la-dept" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. support" />
              <label htmlFor="la-viewers">Viewer user_dpt ids (optional, comma-separated)</label>
              <input
                id="la-viewers"
                value={viewerIdsRaw}
                onChange={(e) => setViewerIdsRaw(e.target.value)}
                placeholder="202, 303"
              />
              <div className="live-assist-live-actions">
                <button type="button" className="primary" disabled={busy} onClick={() => void handleCreate()}>
                  Create session
                </button>
              </div>
            </div>
          ) : (
            <div className="live-assist-live-form">
              <div className="live-assist-live-meta">
                <div>
                  <strong>Session</strong> {sessionId}
                </div>
                <div>
                  Mode {session?.mode} · turns {session?.turn_count ?? 0} · {session?.status}
                  {textBotMode
                    ? ' · text bot'
                    : session?.audio_ingress_mode === 'backend_streaming'
                      ? ' · server streaming'
                      : ''}
                </div>
              </div>
              <div className="live-assist-live-actions">
                {!autoServerVoiceBot && !textBotMode && (
                  <button type="button" disabled={busy || audioActive} onClick={() => void handleStartAudio()}>
                    Start audio ingress
                  </button>
                )}
                {autoServerVoiceBot && !serverMediaActive && (
                  <button type="button" disabled={busy} onClick={() => void handleStartAudio()}>
                    Start audio ingress
                  </button>
                )}
                <button type="button" disabled={busy || !audioActive} onClick={() => void handleStopAudio()}>
                  Stop audio
                </button>
                <button type="button" disabled={busy} onClick={() => void refreshSession()}>
                  Refresh status
                </button>
                <button type="button" disabled={busy} onClick={() => void handleClose()}>
                  Close session
                </button>
              </div>

              <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

              <label>Manual turn (HTTP POST /turns)</label>
              {session?.mode !== 'voice_bot' && (
                <select value={manualSpeaker} onChange={(e) => setManualSpeaker(e.target.value as SttSpeaker)}>
                  <option value="customer">customer</option>
                  <option value="agent">agent</option>
                </select>
              )}
              <textarea value={manualText} onChange={(e) => setManualText(e.target.value)} placeholder="Utterance text" />
              <div className="live-assist-live-actions">
                <button type="button" className="primary" disabled={busy || !manualText.trim()} onClick={() => void handleManualTurn()}>
                  Send manual turn
                </button>
              </div>

              <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

              {textBotMode ? (
                <>
                  <label>Text bot (audio off)</label>
                  <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 8px' }}>
                    STT and TTS are disabled (<code>live_assist.voice_bot_audio_enabled=false</code>).
                    Use Manual turn above to drive the conversation.
                  </p>
                </>
              ) : serverStreamingMode ? (
                <>
                  <label>Server STT{session?.mode === 'voice_bot' ? ' + TTS' : ''} (WebSocket)</label>
                  <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 8px' }}>
                    {autoServerVoiceBot
                      ? 'Mic and WebSocket start automatically when you create the session. Speak as the customer; the server transcribes your voice and drives the voice bot.'
                      : agentAssistServerStreaming
                        ? 'Mic audio streams to the API over WebSocket. Choose who is on the mic below; server STT uses that speaker (not diarization). Recording is saved when the WebSocket closes.'
                        : 'Mic audio streams to the API over WebSocket. The server runs STT and voice-bot TTS; bot speech plays from streamed MP3.'}
                  </p>
                  {agentAssistServerStreaming && (
                    <select
                      value={sttSpeaker}
                      onChange={(e) => setSttSpeaker(e.target.value as SttSpeaker)}
                      disabled={!serverMediaActive}
                    >
                      <option value="customer">customer</option>
                      <option value="agent">agent</option>
                    </select>
                  )}
                  {serverMediaStatus && (
                    <p style={{ fontSize: '0.85rem', color: '#334155', margin: '0 0 8px' }}>{serverMediaStatus}</p>
                  )}
                  {serverMediaActive && (
                    <div className="live-assist-live-actions">
                      <button type="button" disabled={busy} onClick={() => setMicMuted((m) => !m)}>
                        {micMuted ? 'Unmute mic' : 'Mute mic'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label>Browser captions → ingest-text (start audio ingress first)</label>
                  <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 8px' }}>
                    {sttSupported
                      ? 'Uses Web Speech API (e.g. Chrome). Mic permission required. Stopping captions, stopping audio, or closing the session waits briefly for a final result, then sends any remaining partial line so the tail of the call is not dropped from the transcript or FSM. For audio_mic, MediaRecorder runs until Close session; upload is audios/{interaction_id}.webm for replay.'
                      : 'Not supported in this browser.'}
                  </p>
                  <select value={sttSpeaker} onChange={(e) => setSttSpeaker(e.target.value as SttSpeaker)} disabled={!audioActive}>
                    <option value="customer">customer</option>
                    <option value="agent">agent</option>
                  </select>
                  <div className="live-assist-live-actions">
                    {!sttOn ? (
                      <button
                        type="button"
                        className="primary"
                        disabled={busy || !audioActive || !sttSupported}
                        onClick={startStt}
                      >
                        Start mic captions
                      </button>
                    ) : (
                      <button type="button" disabled={busy} onClick={() => void finalizeBrowserSttFlushPending()}>
                        Stop mic captions
                      </button>
                    )}
                  </div>
                </>
              )}

              {(sttInterim.trim().length > 0 || voiceLines.length > 0) && (
                <div className="live-assist-stt-log" aria-live="polite">
                  <div className="live-assist-stt-log-title">Voice transcript</div>
                  <div className="live-assist-stt-log-body">
                    {voiceLines.map((line) => (
                      <div key={line.id} className="live-assist-stt-line">
                        <span className="live-assist-stt-speaker">{line.speaker}</span>
                        <span className="live-assist-stt-text">{line.text}</span>
                        {line.moments && line.moments.length > 0 && (
                          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {line.moments.map((name) => (
                              <span
                                key={name}
                                style={{
                                  fontSize: '0.7rem',
                                  background: '#e0f2fe',
                                  color: '#075985',
                                  borderRadius: 4,
                                  padding: '1px 6px',
                                }}
                              >
                                {name}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    ))}
                    {sttOn && sttInterim.trim().length > 0 && (
                      <div className="live-assist-stt-line live-assist-stt-interim">
                        <span className="live-assist-stt-speaker">{sttSpeaker}</span>
                        <span className="live-assist-stt-text">{sttInterim}</span>
                      </div>
                    )}
                    {serverStreamingMode && sttInterim.trim().length > 0 && (
                      <div className="live-assist-stt-line live-assist-stt-interim">
                        <span className="live-assist-stt-speaker">
                          {agentAssistServerStreaming ? sttSpeaker : 'customer'}
                        </span>
                        <span className="live-assist-stt-text">{sttInterim}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {mode === 'demo_replay' && (
                <>
                  {(sessionInputSource === 'audio_file' ||
                    sessionInputSource === 'audio_mic' ||
                    sessionInputSource === 'external_stream') && (
                    <>
                      <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>From audio file (server transcribes)</div>
                      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 8px', lineHeight: 1.45 }}>
                        Path to an audio file on the API server (e.g. under <code>audios_dir</code>). Uses STT; creates an
                        interaction from the recording.
                      </p>
                      <label htmlFor="la-transcribe-path">Audio path on server</label>
                      <input
                        id="la-transcribe-path"
                        value={transcribePath}
                        onChange={(e) => setTranscribePath(e.target.value)}
                        placeholder="e.g. my_call.wav or relative/path.wav"
                      />
                      <div className="live-assist-live-actions">
                        <button type="button" disabled={busy || !transcribePath.trim()} onClick={() => void handleTranscribe()}>
                          Transcribe audio file
                        </button>
                      </div>
                    </>
                  )}
                  {(sessionInputSource === 'text_manual' ||
                    sessionInputSource === 'audio_mic' ||
                    sessionInputSource === 'external_stream') && (
                    <>
                      <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>From transcript text file (no STT)</div>
                      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 8px', lineHeight: 1.45 }}>
                        Path to a <strong>.txt</strong> with Assembly-style lines:{' '}
                        <code>start_ms : end_ms : Agent|Customer : utterance</code>. Skips transcription; use when you already
                        have a transcript (or use <strong>Manual turn</strong> above for one line at a time).
                      </p>
                      <label htmlFor="la-transcript-ingest-path">Transcript path on server</label>
                      <input
                        id="la-transcript-ingest-path"
                        value={transcriptIngestPath}
                        onChange={(e) => setTranscriptIngestPath(e.target.value)}
                        placeholder="e.g. transcripts/my_call.txt"
                      />
                      <div className="live-assist-live-actions">
                        <button
                          type="button"
                          disabled={busy || !transcriptIngestPath.trim()}
                          onClick={() => void handleIngestTranscriptFile()}
                        >
                          Ingest transcript file
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {sessionError && <div className="live-assist-error" style={{ marginTop: 12, textAlign: 'left' }}>{sessionError}</div>}
          {infoBanner && (
            <div style={{ marginTop: 12, fontSize: '0.85rem', color: '#475569', lineHeight: 1.45 }}>{infoBanner}</div>
          )}
        </div>

        <div className="live-assist-live-main">
          {voiceBotLog.length > 0 && (() => {
            const latest = voiceBotLog[voiceBotLog.length - 1];
            const history = voiceBotLog.slice(0, -1);
            return (
              <div className="live-assist-voicebot-panel">
                <div className="live-assist-voicebot-header">
                  <span className="live-assist-voicebot-badge">🤖 Voice Bot</span>
                  {latest.focusAction && (
                    <span className="live-assist-voicebot-focus">
                      step: <strong>{latest.focusAction}</strong>
                    </span>
                  )}
                  <span className="live-assist-voicebot-turn">turn {latest.turn}</span>
                </div>
                <div className="live-assist-voicebot-speech">{latest.speech}</div>
                <div className="live-assist-voicebot-footer">
                  {latest.actionsPerformed.length > 0 && (
                    <span className="live-assist-voicebot-actions">
                      ✓ {latest.actionsPerformed.join(', ')}
                    </span>
                  )}
                  <button
                    type="button"
                    className="live-assist-voicebot-copy"
                    onClick={() => void navigator.clipboard.writeText(latest.speech)}
                    title="Copy speech to clipboard"
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    className="live-assist-voicebot-speak"
                    disabled={!voiceBotTtsActive}
                    onClick={() => speakVoiceBotText(latest.speech)}
                    title={
                      textBotMode || !voiceBotAudioEnabled
                        ? 'Audio disabled in live_assist.voice_bot_audio_enabled (config.yaml)'
                        : serverStreamingMode
                          ? 'Bot speech is played from server TTS over WebSocket'
                          : !ttsSupported
                            ? 'Speech synthesis not supported in this browser'
                            : 'Replay bot speech'
                    }
                  >
                    🔊 Speak
                  </button>
                </div>
                {history.length > 0 && (
                  <details className="live-assist-voicebot-history">
                    <summary>History ({history.length} earlier response{history.length !== 1 ? 's' : ''})</summary>
                    <div className="live-assist-voicebot-history-list">
                      {[...history].reverse().map((entry, i) => (
                        <div key={i} className="live-assist-voicebot-history-entry">
                          <span className="live-assist-voicebot-history-turn">T{entry.turn}</span>
                          <span className="live-assist-voicebot-history-text">{entry.speech}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })()}

          <div className="live-assist-live-card">
            <div
              className="live-assist-agent-assist-toggle"
              role="group"
              aria-label="Agent assist layout"
            >
              <span className="live-assist-agent-assist-toggle-label">Assist view</span>
              <button
                type="button"
                className={agentAssistUiMode === 'compact' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('compact')}
              >
                Compact card
              </button>
              <button
                type="button"
                className={agentAssistUiMode === 'focus' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('focus')}
              >
                Focus live
              </button>
              <button
                type="button"
                className={agentAssistUiMode === 'focus_compact' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('focus_compact')}
              >
                Focus live compact
              </button>
              <button
                type="button"
                className={agentAssistUiMode === 'timeline' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('timeline')}
              >
                Timeline
              </button>
            </div>
            <div className="live-assist-live-card-body">
              {agentAssistUiMode === 'compact' ? (
                currentTurnForAssist ? (
                  <AgentAssistCard card={currentTurnForAssist.agent_card} />
                ) : (
                  <div className="live-assist-live-card-empty">No turns yet.</div>
                )
              ) : agentAssistUiMode === 'focus' || agentAssistUiMode === 'focus_compact' ? (
                <AgentAssistLiveCard
                  allTurns={allTurns}
                  currentTurnNumber={currentTurnNumber}
                  heroVariant={agentAssistUiMode === 'focus_compact' ? 'compact' : 'default'}
                />
              ) : (
                <LiveAssistCard allTurns={allTurns} currentTurnNumber={currentTurnNumber} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAssistLive;
